import * as Sentry from '@sentry/browser';
import { type TracesSamplerSamplingContext } from '@sentry/core';
import { APP_ENV } from '../utils/environment';

const isSentryEnabled = APP_ENV === 'production' || APP_ENV === 'test';

const tracesSampler = ({ parentSampled, name }: TracesSamplerSamplingContext) => {
  const transaction = name.toLowerCase();

  // If the transaction has a parent span, inherit its sampling decision to ensure the trace is complete.
  if (parentSampled !== undefined) {
    return parentSampled;
  }

  // Keep non-production environments easier to inspect.
  if (APP_ENV !== 'production') {
    return 0.1;
  }

  // This is to debug issue: "locationDwkeys is empty, this should not happen".
  // Testing higher sampling to debug trace context between frontend and backend.
  // To validate this, check that baggage header contains sentry-sampled=true
  // and that frontend issue contains the same trace id as the backend issue.
  if (transaction === '/followup/data/environment') return 1;

  // Highest-volume routes are sampled lower to reduce trace volume and cost.
  if (transaction === '/') return 0.02;
  if (transaction === '/pickups' || transaction.startsWith('/pickups/')) return 0.04;
  if (transaction === '/followup' || transaction.startsWith('/followup/data/')) return 0.08;
  if (transaction === '/admin' || transaction.startsWith('/admin/')) return 0.08;

  // Default sampling rate for other transactions.
  return 0.2;
};

export const initSentry = (dsn: string, release?: string) => {
  Sentry.init({
    dsn,
    release,
    integrations: [Sentry.browserTracingIntegration()],
    tracePropagationTargets: [
      'localhost',
      /^\//,
      'api.dev.stenarecycling.cloud',
      'api.test.stenarecycling.cloud',
      'api.prod.stenametall.cloud',
      'api.stenarecycling.com',
    ],
    environment: APP_ENV,
    tracesSampler,
    enabled: isSentryEnabled,
    beforeSend(event) {
      scrubEvents(event);

      return ignoreErrors(event);
    },
  });
};

const scrubEvents = (event: Sentry.ErrorEvent) => {
  if (event.request?.headers) {
    delete event.request.headers.Authorization;
    delete event.request.headers.authorization;
    delete event.request.headers.Cookie;
    delete event.request.headers.cookie;
  }

  if (event.extra?.headers) {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const headers = event.extra.headers as Record<string, string>;

    delete headers.Authorization;
    delete headers.authorization;
    delete headers.Cookie;
    delete headers.cookie;
  }
};

const ignoreErrors = (event: Sentry.ErrorEvent) => {
  const exception = event.exception?.values?.[0];
  const errorType = exception?.type?.toLowerCase() ?? '';
  const errorMessage = exception?.value?.toLowerCase() ?? '';

  // prettier-ignore
  if (
    (errorType === 'aborterror' && errorMessage.includes('signal is aborted without reason')) ||
    (errorType === 'aborterror' && errorMessage.includes('the user aborted a request')) ||
    (errorType === 'aborterror' && errorMessage.includes('the operation was aborted')) ||
    (errorType === 'aborterror' && errorMessage.includes('fetch is aborted')) ||
    (errorType === 'aborterror' && errorMessage.includes('request signal is aborted')) ||
    (errorType === 'typeerror' && errorMessage.includes('failed to fetch')) ||
    (errorType === 'typeerror' && errorMessage.includes('load failed')) ||
    (errorType === 'typeerror' && errorMessage.includes('avbruten')) ||
    (errorType === 'typeerror' && errorMessage.includes('networkerror when attempting to fetch resource')) ||
    (errorType === 'error' && errorMessage.includes('not found')) ||
    (errorType === 'error' && errorMessage.includes('the user aborted a request'))
  ) {
    return null;
    // Ignore these errors because they are often caused by users not having internet connectivity, navigating away from the page, or canceling requests,
    // and do not indicate a problem with the application itself.
  }

  return event;
};
